import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Phone, Mail, MapPin, Clock, Send, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { getContactInfo, addContactMessage } from '../services/firebaseService';

const ContactPage = () => {
  const [contactInfo, setContactInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  useEffect(() => {
    fetchContactInfo();
  }, []);

  const fetchContactInfo = async () => {
    try {
      const data = await getContactInfo();
      setContactInfo(data);
    } catch (error) {
      console.error('Error fetching contact info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Zəhmət olmasa bütün vacib sahələri doldurun');
      return;
    }

    setSubmitting(true);
    try {
      await addContactMessage(formData);
      toast.success('Mesajınız uğurla göndərildi!');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Mesaj göndərilə bilmədi. Zəhmət olmasa yenidən cəhd edin.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#1e3a5f] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] pt-24">
      {/* Header */}
      <div className="bg-[#1e3a5f] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center text-sm text-white/60 mb-4">
            <Link to="/" className="hover:text-white">Ana Səhifə</Link>
            <ChevronRight className="h-4 w-4 mx-2" />
            <span className="text-white">Əlaqə</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white">
            Əlaqə
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Contact Information */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="card-hover">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="w-12 h-12 bg-[#1e3a5f]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Phone className="h-6 w-6 text-[#1e3a5f]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#1e3a5f] mb-1">Telefon</h3>
                  <p className="text-slate-600">{contactInfo?.phone || '+994 50 655 55 01'}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="card-hover">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="w-12 h-12 bg-[#3b82f6]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="h-6 w-6 text-[#3b82f6]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#1e3a5f] mb-1">Email</h3>
                  <p className="text-slate-600">{contactInfo?.email || 'info@polymax.az'}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="card-hover">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="w-12 h-12 bg-[#25d366]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-6 w-6 text-[#25d366]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#1e3a5f] mb-1">Ünvan</h3>
                  <p className="text-slate-600">{contactInfo?.address || 'Bakı şəhəri, Azərbaycan'}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="card-hover">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#1e3a5f] mb-1">İş Saatları</h3>
                  <p className="text-slate-600">{contactInfo?.working_hours || 'Bazar ertəsi - Cümə: 09:00 - 18:00'}</p>
                </div>
              </CardContent>
            </Card>

            <a
              href={`https://wa.me/${(contactInfo?.whatsapp || '+994506555501').replace(/\D/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <Card className="bg-[#25d366] hover:bg-[#25d366]/90 transition-colors cursor-pointer">
                <CardContent className="p-6 flex items-center gap-4 text-white">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <MessageCircle className="h-6 w-6" fill="white" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">WhatsApp ilə Yazın</h3>
                    <p className="text-white/80 text-sm">{contactInfo?.whatsapp || '+994 50 655 55 01'}</p>
                  </div>
                </CardContent>
              </Card>
            </a>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-[#1e3a5f] mb-6">
                  Bizə Mesaj Göndərin
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Ad Soyad *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Adınızı daxil edin"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="email@example.com"
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefon</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+994 XX XXX XX XX"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message">Mesaj *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Mesajınızı yazın..."
                      rows={5}
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    size="lg"
                    disabled={submitting}
                    className="w-full md:w-auto bg-[#1e3a5f] hover:bg-[#1e3a5f]/90"
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                        Göndərilir...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Göndər
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
